import React, { useEffect, useRef, useState } from "react";
import { CLOUDINARY_CLOUD } from "../constants/CloudinaryConstants";
import { HOST } from "../constants/HostConstants";
import { MdDragIndicator, MdClose } from "react-icons/md";

const UploadWidget = ({ setUploadedItems, userId, propertyId }) => {
  const cloudinaryRef = useRef();
  const widgetRef = useRef();
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => {
    let mounted = true;
    const initWidget = async () => {
      cloudinaryRef.current = window.cloudinary;
      try {
        // Build folder path: user_{userId}/property_{propertyId} or just user_{userId}
        const folder = propertyId
          ? `user_${userId}/property_${propertyId}`
          : `user_${userId}`;

        const res = await fetch(
          `${HOST}/api/user/cloudinarySign?folder=${encodeURIComponent(folder)}`
        );
        if (!res.ok) throw new Error("sign failed");
        const sign = await res.json();
        const widgetOptions = {
          cloudName: sign.cloud || CLOUDINARY_CLOUD,
          uploadSignature: sign.signature,
          uploadSignatureTimestamp: sign.timestamp,
          apiKey: sign.api_key,
          folder: sign.folder,
          // Auto compression during upload
          transformation: [
            {
              quality: "auto:good", // Automatic quality optimization
              fetch_format: "auto", // Auto-select format (AVIF, WebP, JPG, PNG)
              width: 1920, // Max width
              crop: "scale",
            },
          ],
          // Eager transformations (pre-generate optimized versions)
          eager: [
            {
              width: 600,
              height: 600,
              crop: "fill",
              gravity: "auto",
              quality: 80,
              fetch_format: "auto",
            },
          ],
        };
        if (sign.preset) widgetOptions.uploadPreset = sign.preset;
        widgetRef.current = cloudinaryRef.current.createUploadWidget(
          widgetOptions,
          function (error, result) {
            if (result && result["event"] === "success") {
              const newPhoto = {
                path: result["info"]["path"],
                url: result["info"]["secure_url"],
                thumbnail: result["info"]["thumbnail_url"],
                id: result["info"]["public_id"],
              };
              setPhotos((current) => [...current, newPhoto]);
            }
          }
        );
        if (mounted) setReady(true);
      } catch (err) {
if (mounted)
          setError("Unable to initialize signed upload widget. Contact admin.");
      }
    };
    if (userId) {
      initWidget();
    }
    return () => {
      mounted = false;
    };
  }, [userId, propertyId]);

  // Update parent component whenever photos array changes
  useEffect(() => {
    setUploadedItems(photos.map((p) => p.path));
  }, [photos, setUploadedItems]);

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newPhotos = [...photos];
    const draggedPhoto = newPhotos[draggedIndex];
    newPhotos.splice(draggedIndex, 1);
    newPhotos.splice(index, 0, draggedPhoto);

    setPhotos(newPhotos);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleDelete = (index) => {
    setPhotos((current) => current.filter((_, i) => i !== index));
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="text-center flex flex-col gap-4">
        <h1>Upload images with any format.</h1>
        <p className="text-sm text-gray-600">
          Drag and drop photos below to reorder them. The first photo will be
          the main listing image.
        </p>
        <button
          className="px-8 py-2 rounded-10 hover:cursor-pointer hover:scale-105 transition-all bg-blue-500 text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          onClick={() => {
            if (!ready || !widgetRef.current) {
              alert(error || "Upload is not available.");
              return;
            }
            widgetRef.current.open();
          }}
          disabled={!ready}
        >
          {ready ? "Upload media" : "Upload unavailable"}
        </button>
      </div>

      {photos.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-lg">
              Uploaded Photos ({photos.length})
            </h2>
            <p className="text-xs text-gray-500">
              {photos.length === 1 ? "1st" : "1st"} photo will be the main image
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {photos.map((photo, index) => (
              <div
                key={photo.id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`relative group rounded-lg overflow-hidden border-2 ${
                  draggedIndex === index
                    ? "border-blue-500 opacity-50"
                    : "border-gray-200"
                } hover:border-blue-400 transition-all cursor-move`}
              >
                {/* Position indicator */}
                <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full z-10">
                  {index + 1}
                </div>

                {/* Delete button */}
                <button
                  onClick={() => handleDelete(index)}
                  className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10 hover:bg-red-600"
                  title="Delete photo"
                >
                  <MdClose size={16} />
                </button>

                {/* Drag indicator */}
                <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-gray-800 bg-opacity-70 text-white px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity z-10 flex items-center gap-1">
                  <MdDragIndicator size={16} />
                  <span className="text-xs">Drag to reorder</span>
                </div>

                {/* Photo */}
                <img
                  src={photo.url}
                  alt={`Upload ${index + 1}`}
                  className="w-full h-40 object-cover"
                  draggable={false}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadWidget;
